/**
 * Remux de Opus-em-WebM para Opus-em-Ogg, no NAVEGADOR, sem reencode.
 *
 * Por que o front faz isto: no WhatsApp Cloud a **nota de voz** (bolha com
 * onda, play e velocidade) só acontece com `audio/ogg` + OPUS. O Firefox grava
 * Ogg direto; Chrome e Edge gravam `audio/webm;codecs=opus` — os MESMOS pacotes
 * Opus, só que noutra embalagem. Trocar a embalagem aqui é o que mantém o
 * servidor fora do negócio de mexer em mídia: ele recebe o arquivo já no
 * formato final e não toca num byte.
 *
 * Este arquivo é a porta do `webm-opus-ogg.util.ts` do whiz-server, que segue
 * existindo lá como REDE para quem envia áudio sem navegador (bot de fluxo e a
 * rota pública da API) e roda numa thread própria. Os dois têm de concordar:
 * mexeu num, mexa no outro.
 *
 * Sem dependências e sem `Uint8Array` — só `Uint8Array`/`DataView`.
 *
 * LIMITE CONHECIDO: o WebM não diz quantas amostras do último pacote são
 * padding do encoder, então a saída não tem "end trim" — fica ~13 ms mais
 * longa. É padding do próprio encoder (silêncio), inaudível numa nota de voz.
 * O início é exato: o `pre-skip` do OpusHead viaja no cabeçalho e o
 * decodificador o descarta.
 */

/* ──────────────────────────── bytes ──────────────────────────── */

const vista = (b: Uint8Array): DataView => new DataView(b.buffer, b.byteOffset, b.byteLength);

const ascii = (b: Uint8Array, inicio: number, fim: number): string => {
    let out = '';
    for (let i = inicio; i < Math.min(fim, b.length); i++) out += String.fromCharCode(b[i]);
    return out;
};

const textoUtf8 = (txt: string): Uint8Array => new TextEncoder().encode(txt);

/** Inteiro big-endian de `tamanho` bytes (EBML guarda assim). */
const uintBE = (b: Uint8Array, offset: number, tamanho: number): number => {
    let valor = 0;
    for (let i = 0; i < tamanho; i++) valor = valor * 256 + b[offset + i];
    return valor;
};

const concat = (partes: Uint8Array[]): Uint8Array => {
    const total = partes.reduce((soma, p) => soma + p.length, 0);
    const saida = new Uint8Array(total);
    let offset = 0;
    for (const parte of partes) {
        saida.set(parte, offset);
        offset += parte.length;
    }
    return saida;
};


/* ──────────────────────────── EBML (WebM) ──────────────────────────── */

const ID_SEGMENT = 0x18538067;
const ID_TRACKS = 0x1654ae6b;
const ID_TRACKENTRY = 0xae;
const ID_TRACKNUMBER = 0xd7;
const ID_CODECID = 0x86;
const ID_CODECPRIVATE = 0x63a2;
const ID_CLUSTER = 0x1f43b675;
const ID_SIMPLEBLOCK = 0xa3;
const ID_BLOCKGROUP = 0xa0;
const ID_BLOCK = 0xa1;

/** Elementos "mestres": têm filhos e são percorridos EM LINHA (ver `percorrer`). */
const MESTRES = new Set<number>([ID_SEGMENT, ID_TRACKS, ID_TRACKENTRY, ID_CLUSTER, ID_BLOCKGROUP]);

/** Assinatura EBML — todo WebM/Matroska começa com ela. */
const MAGIC_EBML = 0x1a45dfa3;

interface Vint {
  valor: number;
  bytes: number;
  /** Tamanho "desconhecido" (todos os bits de dado em 1) — o que o navegador emite. */
  desconhecido: boolean;
}

/**
 * Lê um inteiro de largura variável do EBML.
 *
 * `comMarcador` mantém os bits de comprimento no valor — é assim que os IDs de
 * elemento são escritos na especificação (`0xA3`, `0x1F43B675`); para TAMANHOS
 * o marcador sai e sobra o número.
 */
function lerVint(buf: Uint8Array, pos: number, comMarcador: boolean): Vint | null {
  if (pos >= buf.length) return null;
  const primeiro = buf[pos];
  if (primeiro === 0) return null; // largura > 8 bytes: não ocorre em áudio
  let bytes = 1;
  for (let mascara = 0x80; !(primeiro & mascara); mascara >>= 1) bytes++;
  if (pos + bytes > buf.length) return null;

  const mascaraDado = 0xff >> bytes;
  let valor = comMarcador ? primeiro : primeiro & mascaraDado;
  let todosUns = (primeiro & mascaraDado) === mascaraDado;
  for (let i = 1; i < bytes; i++) {
    valor = valor * 256 + buf[pos + i];
    if (buf[pos + i] !== 0xff) todosUns = false;
  }
  return { valor, bytes, desconhecido: !comMarcador && todosUns };
}

interface Elemento {
  id: number;
  inicio: number;
  fim: number;
  mestre: boolean;
}

/**
 * Varredura LINEAR do arquivo, descendo nos mestres em vez de recursar.
 *
 * Descer em linha é o que permite atravessar `Segment`/`Cluster` de tamanho
 * DESCONHECIDO — e é exatamente isso que o `MediaRecorder` do navegador emite,
 * porque ele escreve enquanto grava e não sabe o tamanho final. Uma travessia
 * recursiva que também varresse o pai contaria cada bloco duas vezes.
 */
function* percorrer(buf: Uint8Array, inicio: number, fim: number): Generator<Elemento> {
  let pos = inicio;
  while (pos < fim) {
    const id = lerVint(buf, pos, true);
    if (!id) return;
    const tamanho = lerVint(buf, pos + id.bytes, false);
    if (!tamanho) return;

    const dadoInicio = pos + id.bytes + tamanho.bytes;
    const mestre = MESTRES.has(id.valor);
    const dadoFim = tamanho.desconhecido ? fim : Math.min(fim, dadoInicio + tamanho.valor);

    yield { id: id.valor, inicio: dadoInicio, fim: dadoFim, mestre };
    pos = mestre ? dadoInicio : dadoFim;
  }
}

interface FaixaOpus {
  /** `CodecPrivate` da faixa — é o próprio OpusHead, reaproveitado no Ogg. */
  opusHead: Uint8Array | null;
  /** Pacotes Opus na ordem em que estão no arquivo. */
  pacotes: Uint8Array[];
}

/** Extrai o OpusHead e os pacotes Opus do WebM. */
function extrairFaixaOpus(buf: Uint8Array): FaixaOpus {
  let opusHead: Uint8Array | null = null;
  let numeroDaFaixa: number | null = null;
  const pacotes: Uint8Array[] = [];

  let pendente: { numero: number | null; codec: string | null; privado: Uint8Array | null } | null = null;
  const fecharFaixa = (): void => {
    if (pendente?.codec?.startsWith('A_OPUS')) {
      numeroDaFaixa = pendente.numero;
      opusHead = pendente.privado;
    }
    pendente = null;
  };

  const lerBloco = (inicio: number, fim: number): void => {
    const faixa = lerVint(buf, inicio, false);
    if (!faixa) return;
    let p = inicio + faixa.bytes;
    // timecode relativo (2) + flags (1)
    if (p + 3 > fim) return;
    if (numeroDaFaixa !== null && faixa.valor !== numeroDaFaixa) return;
    p += 2;
    const flags = buf[p];
    p += 1;

    const lacing = (flags & 0x06) >> 1;
    if (lacing === 0) {
      pacotes.push(buf.subarray(p, fim));
      return;
    }

    // Lacing não aparece no que o navegador grava (um quadro por bloco), mas um
    // arquivo vindo de outro mux pode usar — tratar é barato e evita áudio picado.
    const quantidade = buf[p] + 1;
    p += 1;
    const tamanhos: number[] = [];
    if (lacing === 2) {
      const cada = Math.floor((fim - p) / quantidade);
      for (let i = 0; i < quantidade - 1; i++) tamanhos.push(cada);
    } else if (lacing === 1) {
      for (let i = 0; i < quantidade - 1; i++) {
        let tamanho = 0;
        while (buf[p] === 0xff) {
          tamanho += 255;
          p++;
        }
        tamanho += buf[p];
        p++;
        tamanhos.push(tamanho);
      }
    } else {
      const primeiro = lerVint(buf, p, false);
      if (!primeiro) return;
      p += primeiro.bytes;
      tamanhos.push(primeiro.valor);
      let anterior = primeiro.valor;
      for (let i = 1; i < quantidade - 1; i++) {
        const delta = lerVint(buf, p, false);
        if (!delta) return;
        p += delta.bytes;
        const vies = Math.pow(2, 7 * delta.bytes - 1) - 1;
        anterior += delta.valor - vies;
        tamanhos.push(anterior);
      }
    }
    let usado = 0;
    for (const tamanho of tamanhos) {
      pacotes.push(buf.subarray(p + usado, p + usado + tamanho));
      usado += tamanho;
    }
    pacotes.push(buf.subarray(p + usado, fim)); // o último quadro ocupa o resto
  };

  for (const el of percorrer(buf, 0, buf.length)) {
    switch (el.id) {
      case ID_TRACKENTRY:
        fecharFaixa();
        pendente = { numero: null, codec: null, privado: null };
        break;
      case ID_TRACKNUMBER:
        if (pendente) pendente.numero = uintBE(buf, el.inicio, Math.max(1, el.fim - el.inicio));
        break;
      case ID_CODECID:
        if (pendente) pendente.codec = ascii(buf, el.inicio, el.fim).replace(/\0+$/, '');
        break;
      case ID_CODECPRIVATE:
        if (pendente) pendente.privado = buf.subarray(el.inicio, el.fim);
        break;
      case ID_CLUSTER:
        fecharFaixa();
        break;
      case ID_SIMPLEBLOCK:
      case ID_BLOCK:
        fecharFaixa();
        lerBloco(el.inicio, el.fim);
        break;
      default:
        break;
    }
  }
  fecharFaixa();

  return { opusHead, pacotes };
}

/* ──────────────────────────── duração do pacote Opus ──────────────────────────── */

const MS_SILK = [10, 20, 40, 60];

/** Duração (ms) de UM quadro, pelo byte TOC (RFC 6716 §3.1). */
function msDoQuadro(toc: number): number {
  const config = toc >> 3;
  if (config < 12) return MS_SILK[config % 4]; // SILK NB/MB/WB
  if (config < 16) return config % 2 === 0 ? 10 : 20; // híbrido SWB/FB
  return [2.5, 5, 10, 20][config % 4]; // CELT
}

/** Amostras a 48 kHz que o pacote decodifica (é a unidade do `granulepos` do Ogg). */
function amostrasDoPacote(pacote: Uint8Array): number {
  if (pacote.length < 1) return 0;
  const toc = pacote[0];
  const codigo = toc & 0x03;
  const quadros =
    codigo === 0 ? 1 : codigo === 1 || codigo === 2 ? 2 : pacote.length > 1 ? pacote[1] & 0x3f : 0;
  return Math.round(quadros * msDoQuadro(toc) * 48);
}

/* ──────────────────────────── Ogg ──────────────────────────── */

/** CRC do Ogg: polinômio 0x04c11db7, sem reflexão e sem xor final. */
const TABELA_CRC = (() => {
  const tabela = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let j = 0; j < 8; j++) r = r & 0x80000000 ? (r << 1) ^ 0x04c11db7 : r << 1;
    tabela[i] = r >>> 0;
  }
  return tabela;
})();

function crcOgg(buf: Uint8Array): number {
  let crc = 0;
  for (let i = 0; i < buf.length; i++) crc = ((crc << 8) ^ TABELA_CRC[((crc >>> 24) ^ buf[i]) & 0xff]) >>> 0;
  return crc >>> 0;
}

const CABECALHO_BOS = 0x02;
const CABECALHO_EOS = 0x04;
/** Uma página Ogg comporta no máximo 255 segmentos de lacing. */
const MAX_SEGMENTOS = 255;
/** Pacotes por página. ~50 é o que os muxers usam; mantém a página curta. */
const PACOTES_POR_PAGINA = 48;

function montarPagina(
  serial: number,
  sequencia: number,
  tipo: number,
  granulepos: number,
  segmentos: number[],
  corpo: Uint8Array,
): Uint8Array {
  const cabecalho = new Uint8Array(27 + segmentos.length);
  const dv = vista(cabecalho);
  cabecalho.set(textoUtf8('OggS'), 0);
  cabecalho[4] = 0; // versão
  cabecalho[5] = tipo;
  dv.setBigInt64(6, BigInt(granulepos), true);
  dv.setUint32(14, serial, true);
  dv.setUint32(18, sequencia, true);
  dv.setUint32(22, 0, true); // CRC entra depois, com o campo zerado
  cabecalho[26] = segmentos.length;
  cabecalho.set(segmentos, 27);

  const pagina = concat([cabecalho, corpo]);
  vista(pagina).setUint32(22, crcOgg(pagina), true);
  return pagina;
}

/** Tabela de lacing de um pacote: blocos de 255 e um resto (que fecha o pacote). */
function lacing(tamanho: number): number[] {
  const segmentos: number[] = [];
  let resto = tamanho;
  while (resto >= 255) {
    segmentos.push(255);
    resto -= 255;
  }
  segmentos.push(resto);
  return segmentos;
}

function montarOpusTags(): Uint8Array {
  const fornecedor = textoUtf8('whiz-front');
  const tamanho = new Uint8Array(4);
  vista(tamanho).setUint32(0, fornecedor.length, true);
  return concat([
    textoUtf8('OpusTags'),
    tamanho,
    fornecedor,
    new Uint8Array(4), // zero comentários
  ]);
}

/* ──────────────────────────── API ──────────────────────────── */

/** `true` quando os bytes começam com a assinatura EBML (WebM/Matroska). */
export function ehContainerWebm(buffer: Uint8Array): boolean {
  return buffer.length >= 4 && vista(buffer).getUint32(0, false) === MAGIC_EBML;
}

/**
 * `true` quando os bytes são um Ogg cuja primeira página é um OpusHead —
 * ou seja, exatamente o que o WhatsApp exige para tocar como nota de voz.
 */
export function ehOggOpus(buffer: Uint8Array): boolean {
  if (buffer.length < 36) return false;
  if (ascii(buffer, 0, 4) !== 'OggS') return false;
  const segmentos = buffer[26];
  const inicioCorpo = 27 + segmentos;
  return ascii(buffer, inicioCorpo, inicioCorpo + 8) === 'OpusHead';
}

/**
 * Troca o container de Opus-em-WebM para Opus-em-Ogg. Os pacotes saem iguais,
 * byte a byte — não há decodificação nem reencode.
 *
 * @throws {Error} quando o WebM não tem faixa Opus (ex.: gravação em outro codec).
 */
export function remuxWebmOpusParaOgg(webm: Uint8Array, serial = 0x5748495a): Uint8Array {
  const { opusHead, pacotes } = extrairFaixaOpus(webm);

  if (!opusHead || opusHead.length < 19 || ascii(opusHead, 0, 8) !== 'OpusHead') {
    throw new Error('WebM sem cabeçalho OpusHead: a faixa de áudio não é Opus.');
  }
  if (pacotes.length === 0) {
    throw new Error('WebM sem pacotes de áudio Opus.');
  }

  const paginas: Uint8Array[] = [];
  let sequencia = 0;

  paginas.push(montarPagina(serial, sequencia++, CABECALHO_BOS, 0, lacing(opusHead.length), opusHead));
  const tags = montarOpusTags();
  paginas.push(montarPagina(serial, sequencia++, 0, 0, lacing(tags.length), tags));

  // `granulepos` = total de amostras decodificáveis até o fim da página, com o
  // `pre-skip` CONTIDO nelas (RFC 7845 §4). Somar o pre-skip aqui o contaria duas
  // vezes e empurraria o áudio ~6 ms para frente.
  let granulepos = 0;
  let segmentos: number[] = [];
  let corpo: Uint8Array[] = [];
  let naPagina = 0;

  const fechar = (ultima: boolean): void => {
    if (segmentos.length === 0) return;
    paginas.push(
      montarPagina(serial, sequencia++, ultima ? CABECALHO_EOS : 0, granulepos, segmentos, concat(corpo)),
    );
    segmentos = [];
    corpo = [];
    naPagina = 0;
  };

  for (let i = 0; i < pacotes.length; i++) {
    const pacote = pacotes[i];
    const segmentosDoPacote = lacing(pacote.length);
    if (segmentos.length + segmentosDoPacote.length > MAX_SEGMENTOS) fechar(false);

    segmentos = segmentos.concat(segmentosDoPacote);
    corpo.push(pacote);
    granulepos += amostrasDoPacote(pacote);
    naPagina++;

    if (naPagina >= PACOTES_POR_PAGINA) fechar(i === pacotes.length - 1);
  }
  fechar(true);

  // O laço pode ter fechado a última página como "não final" se o corte por
  // tamanho caiu no último pacote; o EOS é obrigatório para o leitor saber o fim.
  const ultima = paginas[paginas.length - 1];
  if ((ultima[5] & CABECALHO_EOS) === 0) {
    ultima[5] |= CABECALHO_EOS;
    vista(ultima).setUint32(22, 0, true);
    vista(ultima).setUint32(22, crcOgg(ultima), true);
  }

  return concat(paginas);
}
